// src/pages/OPDReferralbroker.jsx
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import AppConst from "../apis/appconst";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import Select from "react-select";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const OPDReferralbroker = () => {
  const [activeSubTab, setActiveSubTab] = useState("all");

  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  // ---- state
  const [brokerOptions, setBrokerOptions] = useState([]);
  const [brokerId, setBrokerId] = useState("");
  const [brokerLabel, setBrokerLabel] = useState("");
  const [loadingBrokers, setLoadingBrokers] = useState(false);

  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    search_key: "",
    from: getCurrentDate(),
    to: getCurrentDate(),
    opd_broker_id: "",
    opd_broker_name: "",
    records: [],
  });

  // ---- broker payment modal state ----
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [payRow, setPayRow] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payProcessing, setPayProcessing] = useState(false);
  const [payError, setPayError] = useState("");

  // ---------- helpers ----------
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return "";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const getBrokerNumbers = (row) => {
    if (!row) return { commission: 0, paid: 0, due: 0 };
    const num = (v) => {
      const n = parseFloat(v ?? 0);
      return Number.isNaN(n) ? 0 : n;
    };

    const commissionCandidates = [
      "opd_broker_commission",
      "broker_commission",
    ];
    let commission = 0;
    for (const f of commissionCandidates) {
      if (typeof row[f] !== "undefined" && row[f] !== null && row[f] !== "") {
        commission = num(row[f]);
        break;
      }
    }

    const paidCandidates = [
      "opd_broker_commission_paid",
      "broker_commission_paid",
      "broker_paid",
    ];
    let paid = 0;
    for (const f of paidCandidates) {
      if (typeof row[f] !== "undefined" && row[f] !== null && row[f] !== "") {
        paid = num(row[f]);
        break;
      }
    }

    if (
      typeof row.broker_payments_sum !== "undefined" &&
      row.broker_payments_sum !== null
    ) {
      paid = Math.max(paid, num(row.broker_payments_sum));
    }
    if (Array.isArray(row.broker_payments) && row.broker_payments.length) {
      const sum = row.broker_payments.reduce(
        (s, p) => s + (num(p.pay_amount) || num(p.amount) || 0),
        0
      );
      paid = Math.max(paid, sum);
    }

    const due = Math.max(commission - paid, 0);
    return { commission: Number(commission), paid: Number(paid), due: Number(due) };
  };

  const getBrokerStatusInfo = (row) => {
    const { commission, paid, due } = getBrokerNumbers(row);
    if (commission <= 0) return { status: "N/A", className: "bg-secondary" };
    if (paid <= 0) return { status: "Not Paid", className: "bg-danger" };
    if (due <= 0.01) return { status: "Paid", className: "bg-success" };
    return { status: "Partial", className: "bg-warning text-dark" };
  };

  // ---------- load brokers ----------
  const loadBrokers = async (search = "") => {
    try {
      setLoadingBrokers(true);
      const fd = new FormData();
      fd.append("search_key", search || "");
      fd.append("page_no", "none");
      const res = await axios.post(
        AppConst.BASE_URL + AppConst.getBrokerListUrl,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const records =
        res?.data?.records && Array.isArray(res.data.records) ? res.data.records : [];

      const opts = records.map((r) => ({
        value: String(r.broker_id ?? r.id ?? r.user_id ?? ""),
        label:
          `${(r.fname || "").trim()}${r.lname ? " " + (r.lname || "").trim() : ""}`.trim() ||
          (r.mobile || "Unknown"),
        raw: r,
      }));

      setBrokerOptions((prev) => {
        if (brokerId) {
          const exists = opts.find((o) => String(o.value) === String(brokerId));
          if (!exists) {
            return [
              { value: String(brokerId), label: brokerLabel || String(brokerId), raw: null },
              ...opts,
            ];
          }
        }
        return opts;
      });
    } catch (err) {
      console.error("loadBrokers", err);
      setBrokerOptions([]);
    } finally {
      setLoadingBrokers(false);
    }
  };

  useEffect(() => {
    loadBrokers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!brokerId) return;
    setBrokerOptions((prev) => {
      const found = prev.find((opt) => String(opt.value) === String(brokerId));
      if (found) {
        if (brokerLabel && found.label !== brokerLabel) {
          return prev.map((opt) =>
            String(opt.value) === String(brokerId) ? { ...opt, label: brokerLabel } : opt
          );
        }
        return prev;
      }
      return [
        { value: String(brokerId), label: brokerLabel || String(brokerId), raw: null },
        ...prev,
      ];
    });
  }, [brokerId, brokerLabel]);

  // ---------- fetch OPD broker referrals ----------
  const fetchOpdReferrals = async () => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("search_key", formData.search_key || "");

      if (formData.from) fd.append("from", `${formData.from} 00:00:00`);
      if (formData.to) fd.append("to", `${formData.to} 23:59:59`);

      if (formData.opd_broker_id)
        fd.append("opd_broker_id", String(formData.opd_broker_id));
      if (formData.opd_broker_name)
        fd.append("opd_broker_name", formData.opd_broker_name);

      fd.append("page_no", "none");

      const response = await axios.post(
        AppConst.BASE_URL + AppConst.getBrokerReferralOpdUrl,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.data?.status) {
        setFormData((prev) => ({
          ...prev,
          records: response.data.records || [],
        }));
      } else {
        toast.error(response.data?.message || "Failed to fetch data");
        setFormData((prev) => ({ ...prev, records: [] }));
      }
    } catch (error) {
      console.error("❌ Error fetching OPD referrals:", error);
      toast.error(
        "Failed to fetch OPD referrals: " +
          (error.response?.data?.message || error.message)
      );
      setFormData((prev) => ({ ...prev, records: [] }));
    } finally {
      setLoading(false);
    }
  };

  // auto-fetch when filters change
  useEffect(() => {
    fetchOpdReferrals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.from,
    formData.to,
    formData.opd_broker_id,
    formData.opd_broker_name,
    formData.search_key,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    setFormData((prev) => ({
      ...prev,
      search_key: "",
      from: getCurrentDate(),
      to: getCurrentDate(),
      opd_broker_id: "",
      opd_broker_name: "",
    }));
    setBrokerId("");
    setBrokerLabel("");
  };

  // ---------- pay modal ----------
  const openPayModal = (row) => {
    const { due } = getBrokerNumbers(row);
    setPayRow(row);
    setPayAmount(due > 0 ? due.toFixed(2) : "");
    setPayError("");
    setPayModalOpen(true);
  };

  const closePayModal = () => {
    setPayModalOpen(false);
    setPayRow(null);
    setPayAmount("");
    setPayError("");
  };

  const handlePaySubmit = async (e) => {
    e.preventDefault();
    if (!payRow) return;

    const { due } = getBrokerNumbers(payRow);
    const amt = parseFloat(payAmount || 0);

    if (isNaN(amt) || amt <= 0) {
      setPayError("Please enter a valid amount.");
      return;
    }
    // if (amt > due + 0.01) {
    //   setPayError("Amount cannot be more than due.");
    //   return;
    // }

    try {
      setPayProcessing(true);
      setPayError("");

      const fd = new FormData();
      fd.append("opd_id", payRow.opd_id);
      fd.append("opd_broker_id", payRow.opd_broker_id || "");
      fd.append("pay_amount", amt.toFixed(2));

      const res = await axios.post(
        AppConst.BASE_URL + AppConst.payBrokerCommissionOpdUrl,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (res.data?.status) {
        toast.success(res.data?.message || "Broker payment saved.");
        closePayModal();
        fetchOpdReferrals();
      } else {
        setPayError(res.data?.message || "Failed to save payment.");
      }
    } catch (err) {
      console.error("pay broker error", err);
      setPayError(
        err.response?.data?.message || err.message || "Failed to save payment."
      );
    } finally {
      setPayProcessing(false);
    }
  };

  // ---------- totals ----------
  const totals = useMemo(() => {
    const recs = formData.records || [];
    let totalAmount = 0;
    let totalPaid = 0;
    let totalBrokerCommission = 0;
    let totalBrokerPaid = 0;
    let totalBrokerDue = 0;

    recs.forEach((r) => {
      totalAmount += parseFloat(r.opd_final_amt || 0);
      totalPaid += parseFloat(r.opd_paid || 0);

      const { commission, paid, due } = getBrokerNumbers(r);
      totalBrokerCommission += commission;
      totalBrokerPaid += paid;
      totalBrokerDue += due;
    });

    return {
      totalAmount: totalAmount.toFixed(2),
      totalPaid: totalPaid.toFixed(2),
      totalBrokerCommission: totalBrokerCommission.toFixed(2),
      totalBrokerPaid: totalBrokerPaid.toFixed(2),
      totalBrokerDue: totalBrokerDue.toFixed(2),
    };
  }, [formData.records]);

  // ---------- columns ----------
  const columns = useMemo(
    () => [
      { name: "SL No.", selector: (row, index) => index + 1, width: "70px" },
      {
        name: "Date",
        selector: (row) => {
          const dt = new Date(row.opd_datetime);
          return Number.isNaN(dt.getTime()) ? 0 : dt.getTime();
        },
        sortable: true,
        width: "110px",
        cell: (row) => <span>{formatDate(row.opd_datetime)}</span>,
      },
      {
        name: "UHID",
        selector: (row) => row.opd_uhid || "N/A",
        sortable: true,
        wrap: true,
        width: "120px",
      },
      {
        name: "Patient Name",
        selector: (row) =>
          `${row.opd_patient_title || ""} ${row.opd_patient_name || ""}`.trim(),
        sortable: true,
        wrap: true,
        cell: (row) => (
          <div style={{ whiteSpace: "normal" }}>
            {`${row.opd_patient_title || ""} ${row.opd_patient_name || ""}`.trim()}
          </div>
        ),
        width: "180px",
      },
      {
        name: "Mobile",
        selector: (row) => row.opd_patient_mobile || "-",
        wrap: true,
        width: "130px",
      },
      {
        name: "Broker Name",
        selector: (row) => row.opd_broker_name || "N/A",
        sortable: true,
        wrap: true,
        width: "180px",
        cell: (row) => (
          <div style={{ whiteSpace: "normal" }}>{row.opd_broker_name || "N/A"}</div>
        ),
      },
      {
        name: "Total Amount",
        selector: (row) => parseFloat(row.opd_final_amt || 0),
        sortable: true,
        right: true,
        cell: (row) => <span>₹{parseFloat(row.opd_final_amt || 0).toFixed(2)}</span>,
        width: "120px",
      },
      {
        name: "Total Paid",
        selector: (row) => parseFloat(row.opd_paid || 0),
        sortable: true,
        right: true,
        cell: (row) => <span>₹{parseFloat(row.opd_paid || 0).toFixed(2)}</span>,
        width: "120px",
      },
      {
        name: "Broker Comm.",
        selector: (row) => parseFloat(row.opd_broker_commission || 0),
        sortable: true,
        right: true,
        cell: (row) => {
          const { commission } = getBrokerNumbers(row);
          return (
            <span className="fw-bold text-primary">₹{commission.toFixed(2)}</span>
          );
        },
        width: "140px",
      },
      {
        name: "Paid",
        selector: (row) => parseFloat(row.opd_broker_commission_paid || 0),
        sortable: true,
        right: true,
        cell: (row) => {
          const { paid } = getBrokerNumbers(row);
          return <span>₹{paid.toFixed(2)}</span>;
        },
        width: "140px",
      },
      {
        name: "Due",
        selector: (row) => getBrokerNumbers(row).due,
        sortable: true,
        right: true,
        cell: (row) => {
          const { due } = getBrokerNumbers(row);
          return (
            <span className={due > 0 ? "text-danger fw-bold" : ""}>
              ₹{due.toFixed(2)}
            </span>
          );
        },
        width: "140px",
      },
      {
        name: "Status",
        cell: (row) => {
          const info = getBrokerStatusInfo(row);
          return (
            <span className={`badge rounded-pill ${info.className}`}>
              {info.status}
            </span>
          );
        },
        width: "110px",
      },
      {
        name: "Action",
        cell: (row) => {
          const { due } = getBrokerNumbers(row);
          const disabled = due <= 0.01;
          return (
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => openPayModal(row)}
              disabled={disabled}
            >
              {disabled ? "Paid" : "Pay"}
            </button>
          );
        },
        width: "100px",
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
    ],
    []
  );

  const customStyles = {
    headCells: {
      style: {
        backgroundColor: "#f1f5fb",
        color: "#405189",
        fontSize: "14px",
        fontWeight: 600,
      },
    },
    rows: { style: { fontSize: "14px", minHeight: "48px" } },
    cells: {
      style: {
        paddingTop: "8px",
        paddingBottom: "8px",
        paddingLeft: "10px",
        paddingRight: "10px",
      },
    },
  };

  const reactSelectStyles = {
    control: (base) => ({ ...base, minHeight: "38px", fontSize: "14px" }),
    option: (base) => ({ ...base, fontSize: "14px" }),
  };

  // ---------- Export PDF ----------
  const handleExportPDF = () => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(16);
    doc.setFont(undefined, "bold");
    doc.text("OPD Broker Commission Report", pageWidth / 2, 15, { align: "center" });

    doc.setFontSize(10);
    doc.setFont(undefined, "normal");
    const subtitle = `Period: ${formatDate(formData.from)} to ${formatDate(formData.to)}`;
    doc.text(subtitle, pageWidth / 2, 22, { align: "center" });

    const tableData = formData.records.map((row, index) => {
      const { commission, paid, due } = getBrokerNumbers(row);
      return [
        index + 1,
        formatDate(row.opd_datetime),
        row.opd_uhid || "N/A",
        `${row.opd_patient_title || ""} ${row.opd_patient_name || ""}`.trim(),
        row.opd_patient_mobile || "-",
        row.opd_broker_name || "N/A",
        commission.toFixed(2),
        paid.toFixed(2),
        due.toFixed(2),
      ];
    });

    tableData.push([
      "",
      "",
      "",
      "",
      "TOTAL",
      "",
      totals.totalBrokerCommission,
      totals.totalBrokerPaid,
      totals.totalBrokerDue,
    ]);

    autoTable(doc, {
      startY: 28,
      head: [
        [
          "SL",
          "Date",
          "UHID",
          "Patient",
          "Mobile",
          "Broker",
          "Commission",
          "Paid",
          "Due",
        ],
      ],
      body: tableData,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: {
        fillColor: [64, 81, 137],
        textColor: 255,
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 22 },
        2: { cellWidth: 25 },
        3: { cellWidth: 35 },
        4: { cellWidth: 25 },
        5: { cellWidth: 35 },
        6: { cellWidth: 25, halign: "right" },
        7: { cellWidth: 25, halign: "right" },
        8: { cellWidth: 25, halign: "right" },
      },
      didParseCell: (data) => {
        if (data.row.index === tableData.length - 1) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [241, 245, 251];
        }
      },
    });

    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.text(
        `Page ${i} of ${totalPages}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: "center" }
      );
      doc.text(
        `Generated on: ${new Date().toLocaleString()}`,
        10,
        doc.internal.pageSize.getHeight() - 10
      );
    }

    doc.save(`OPD_Broker_${getCurrentDate()}.pdf`);
  };

  return (
    <div>
      <ul className="nav nav-pills nav-customs nav-primary mb-3" role="tablist">
        <li className="nav-item">
          
            <a className={`nav-link ${activeSubTab === "all" ? "active" : ""}`}
            onClick={() => setActiveSubTab("all")}
            href="#"
          >
            All
          </a>
        </li>
      </ul>

      <div className="card-body border border-dashed border-end-0 border-start-0">
        <div className="row g-3 align-items-end">
          <div className="col-auto">
            <label className="form-label mb-1">From</label>
            <input
              type="date"
              className="form-control"
              name="from"
              onChange={handleChange}
              value={formData.from}
              style={{ width: "150px" }}
            />
          </div>

          <div className="col-auto">
            <label className="form-label mb-1">To</label>
            <input
              type="date"
              className="form-control"
              name="to"
              onChange={handleChange}
              value={formData.to}
              style={{ width: "150px" }}
            />
          </div>

          <div className="col-auto" style={{ minWidth: "220px" }}>
            <label className="form-label mb-1">Broker</label>
            <Select
              options={brokerOptions}
              value={
                brokerOptions.find(
                  (o) => String(o.value) === String(formData.opd_broker_id)
                ) || null
              }
              onChange={(opt) => {
                if (!opt) {
                  setFormData((prev) => ({
                    ...prev,
                    opd_broker_id: "",
                    opd_broker_name: "",
                  }));
                  setBrokerId("");
                  setBrokerLabel("");
                } else {
                  setFormData((prev) => ({
                    ...prev,
                    opd_broker_id: String(opt.value),
                    opd_broker_name: opt.label,
                  }));
                  setBrokerId(String(opt.value));
                  setBrokerLabel(opt.label);
                }
              }}
              onInputChange={(val, { action }) => {
                if (action === "input-change") loadBrokers(val);
              }}
              isClearable
              placeholder="All Brokers"
              styles={reactSelectStyles}
              isLoading={loadingBrokers}
            />
          </div>

          <div className="col" style={{ minWidth: "250px" }}>
            <label className="form-label mb-1">Search</label>
            <div className="search-box">
              <input
                className="form-control search"
                name="search_key"
                onChange={handleChange}
                value={formData.search_key}
                placeholder="Search by name, UHID, mobile..."
                type="text"
              />
              <i className="ri-search-line search-icon"></i>
            </div>
          </div>

          <div className="col-auto">
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleReset}
              >
                <i className="ri-refresh-line me-1"></i> Clear
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={handleExportPDF}
                disabled={formData.records.length === 0}
              >
                <i className="ri-file-pdf-line me-1"></i> Export PDF
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card-body pt-3">
        <DataTable
          columns={columns}
          data={formData.records}
          customStyles={customStyles}
          pagination
          highlightOnHover
          dense
          persistTableHead
          progressPending={loading}
          noDataComponent={<div className="text-muted py-3">No records found</div>}
        />

        {formData.records.length > 0 && (
          <div className="d-flex justify-content-end mt-3">
            <div className="text-muted" style={{ fontSize: "0.95rem" }}>
              <strong>Total Amount:</strong> ₹{totals.totalAmount} &nbsp;|&nbsp;
              <strong>Total Paid:</strong> ₹{totals.totalPaid} &nbsp;|&nbsp;
              <strong>Broker Commission:</strong> ₹{totals.totalBrokerCommission}{" "}
              &nbsp;|&nbsp;
              <strong>Broker Paid:</strong> ₹{totals.totalBrokerPaid} &nbsp;|&nbsp;
              <strong>Broker Due:</strong> ₹{totals.totalBrokerDue}
            </div>
          </div>
        )}
      </div>

      {/* Broker Payment Modal */}
      {payModalOpen && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.4)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <form onSubmit={handlePaySubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">Pay Broker Commission (OPD)</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closePayModal}
                  ></button>
                </div>
                <div className="modal-body">
                  {payRow && (
                    <>
                      <p className="mb-2">
                        <strong>Broker:</strong>{" "}
                        {payRow.opd_broker_name || "N/A"}
                      </p>
                      <p className="mb-2">
                        <strong>Patient:</strong>{" "}
                        {`${payRow.opd_patient_title || ""} ${
                          payRow.opd_patient_name || ""
                        }`.trim()}{" "}
                        ({payRow.opd_uhid})
                      </p>
                      <p className="mb-2">
                        <strong>Commission:</strong> ₹
                        {getBrokerNumbers(payRow).commission.toFixed(2)} &nbsp;|&nbsp;
                        <strong>Paid:</strong> ₹
                        {getBrokerNumbers(payRow).paid.toFixed(2)} &nbsp;|&nbsp;
                        <strong>Due:</strong> ₹
                        {getBrokerNumbers(payRow).due.toFixed(2)}
                      </p>

                      <div className="mb-3">
                        <label className="form-label">Pay Amount (₹)</label>
                        <input
                          type="number"
                          className="form-control"
                          step="0.01"
                          min="0"
                          value={payAmount}
                          onChange={(e) => setPayAmount(e.target.value)}
                        />
                      </div>

                      {payError && (
                        <div className="alert alert-danger py-2">{payError}</div>
                      )}
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={closePayModal}
                    disabled={payProcessing}
                  >
                    Close
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={payProcessing}
                  >
                    {payProcessing ? "Saving..." : "Save Payment"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OPDReferralbroker;